import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import { Expense } from "../../lib/types";

interface LinkRecurringDialogProps {
  open: boolean;
  availableRoots: Expense[];
  onConfirm: (seriesRootId: string) => Promise<void>;
  onCancel: () => void;
}

const LinkRecurringDialog: React.FC<LinkRecurringDialogProps> = ({
  open,
  availableRoots,
  onConfirm,
  onCancel,
}) => {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const filtered = useMemo(
    () =>
      availableRoots.filter((r) => {
        const q = search.toLowerCase();
        return (
          !q ||
          r.name.toLowerCase().includes(q) ||
          (r.vendor && r.vendor.toLowerCase().includes(q)) ||
          (r.recurrencePattern && r.recurrencePattern.toLowerCase().includes(q))
        );
      }),
    [availableRoots, search]
  );

  const handleConfirm = async () => {
    if (!selectedId) return;
    setConfirming(true);
    try {
      await onConfirm(selectedId);
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (confirming) return;
    setSearch("");
    setSelectedId(null);
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon />
        Link as Recurring
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a recurring series to link the selected expenses to.
        </Typography>
        <TextField
          fullWidth
          placeholder="Search by name, vendor, or pattern..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelectedId(null);
          }}
          autoFocus
          size="small"
          sx={{ mb: 1 }}
        />
        {filtered.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            {search
              ? "No recurring series match your search."
              : "No recurring series found. Create a recurring expense first."}
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
            <List dense disablePadding>
              {filtered.map((root) => (
                <ListItemButton
                  key={root.id}
                  selected={selectedId === root.id}
                  onClick={() => setSelectedId(root.id)}
                  divider
                >
                  <ListItemText
                    primary={root.name}
                    secondary={`${root.recurrencePattern}${root.vendor ? ` — ${root.vendor}` : ""}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedId || confirming}
          startIcon={confirming ? <CircularProgress size={16} /> : <LinkIcon />}
          onClick={handleConfirm}
        >
          {confirming ? "Linking..." : "Link Selected"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkRecurringDialog;
